import cx from "classnames";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { canv } from "@/canv/global";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import { useDebounce } from "@/helpers/hooks/useDebounce";
import { useThrottle } from "@/helpers/hooks/useThrottle";
import { presentationUID } from "@/interface/AppStore";
import { EditableTitleInput } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/EditableTitleInput";
import { useEditableTitle } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/hooks/useEditableTitle";
import { useFramePreviews } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/hooks/useFramePreviews";
import { useFrameSelection } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/hooks/useFrameSelection";
import { useFramesDnD } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/hooks/useFramesDnD";
import { PresentationHeaderView } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/PresentationHeaderView";
import { SlideItem } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/SlideItem";
import { decodeHtml } from "@/shared/lib/decodeHtml";
import type { PresentationData } from "../types";
import styles from "./PresentationView.module.scss";

type PresentationViewProps = {
    presentation: PresentationData;
};

export const PresentationView = ({ presentation }: PresentationViewProps) => {
    const activePresentationUID = useAtomValue(presentationUID);
    const isEditorOrOwner = canv.isBoardModeEditOrOwner();
    const disablePlay = !!activePresentationUID || !isEditorOrOwner || !presentation.frames.length;

    const slidesListRef = useRef<HTMLDivElement | null>(null);
    const [blobs, setBlobs] = useState<Record<string, string>>({});

    const generatePreviews = useFramePreviews(presentation.frames || []);

    const { selected, isSelected, selectFrame, clearSelection, setSelected } = useFrameSelection(
        presentation.frames,
    );

    const {
        dragState,
        handleDragStart,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragOverList,
    } = useFramesDnD({
        frames: presentation.frames,
        selected,
        setSelected,
        onReorder: (newFrames) => {
            const item = canv.canvasController.FindByUID(presentation.UID);
            if (item && item instanceof PresentationWidget) {
                item.reorderSlides(newFrames);
            }
            presentation.frames = newFrames; // пока оставляем твою мутацию
        },
        listRef: slidesListRef,
    });

    const {
        isEditing,
        handleTitleClick,
        handleSubmit: handleTitleSubmit,
        handleCancel: handleTitleCancel,
    } = useEditableTitle({
        onSubmit: (value) => {
            const item = canv.canvasController.FindByUID(presentation.UID);
            if (item && item instanceof PresentationWidget) {
                presentation.title = value;
                item.setName(value);
            }
        },
    });

    const handlePlayPresentation = useCallback(() => {
        const item = canv.canvasController.FindByUID(presentation.UID);
        if (item && item instanceof PresentationWidget) {
            item.play();
        }
    }, [presentation.UID]);

    const handleAddNewFrame = useCallback(
        (index: number) => {
            const item = canv.canvasController.FindByUID(presentation.UID);
            if (item && item instanceof PresentationWidget) {
                if (item) {
                    if (!item.direction) {
                        item.direction = "horizontal";
                    }
                    item.addSlide(undefined, index);
                }
            }
        },
        [presentation.UID],
    );
    const throttledAddFrame = useThrottle(handleAddNewFrame, 500);

    const handleSelectFrame = useCallback(
        (uid: string, e: React.MouseEvent) => {
            selectFrame(uid, e);
            const isShift = e.shiftKey;
            const isMeta = e.metaKey || e.ctrlKey;
            if (!isShift && !isMeta) {
                const item = canv.canvasController.FindByUID(uid);
                if (item) {
                    canv.zoomToSelected([item]);
                }
            }
        },
        [selectFrame],
    );

    const generate = useCallback(async () => {
        const prev = canv.multiManager.disableMultiCursors;
        canv.multiManager.disableMultiCursors = true;

        try {
            const previews = await generatePreviews();
            setBlobs((old) => {
                Object.values(old).forEach((url) => URL.revokeObjectURL(url));
                return previews;
            });
        } finally {
            canv.multiManager.disableMultiCursors = prev;
        }
    }, [generatePreviews]);

    const debouncedGenerate = useDebounce(generate, 800);

    useEffect(() => {
        debouncedGenerate();
    }, [debouncedGenerate]);

    useEffect(() => {
        return () => {
            Object.values(blobs).forEach((url) => URL.revokeObjectURL(url));
        };
    }, [blobs]);
    return (
        <div className={styles.presentationView}>
            <div className={cx(styles.viewHeader, canv.isBoardModeEditOrOwner() && styles.editing)}>
                {isEditing ? (
                    <div className={styles.inputWrapper}>
                        <EditableTitleInput
                            initialValue={decodeHtml(presentation?.title || "")}
                            onSubmit={handleTitleSubmit}
                            onCancel={handleTitleCancel}
                        />
                    </div>
                ) : (
                    <PresentationHeaderView
                        title={decodeHtml(presentation?.title || "")}
                        onTitleClick={handleTitleClick}
                        onPlay={handlePlayPresentation}
                        disablePlay={disablePlay}
                    />
                )}
            </div>

            <div
                ref={slidesListRef}
                className={styles.slidesList}
                onClick={clearSelection}
                onDragOver={isEditorOrOwner ? handleDragOverList : undefined}
            >
                {presentation.frames.map((frame, index) => (
                    <SlideItem
                        imgSrc={blobs[frame.UID]}
                        frame={frame}
                        key={frame.UID}
                        uid={frame.UID}
                        index={index}
                        isSelected={isSelected(frame.UID)}
                        isDragOver={dragState?.uid === frame.UID}
                        dropPosition={dragState?.uid === frame.UID ? dragState.position : undefined}
                        onSelect={handleSelectFrame}
                        onDragStart={isEditorOrOwner ? handleDragStart : undefined}
                        onDragOver={isEditorOrOwner ? handleDragOver : undefined}
                        onDragLeave={isEditorOrOwner ? handleDragLeave : undefined}
                        onDrop={isEditorOrOwner ? handleDrop : undefined}
                        onAddFrame={
                            isEditorOrOwner || canv.isBoardModeEditMini()
                                ? throttledAddFrame
                                : undefined
                        }
                        isDraggable={isEditorOrOwner}
                    />
                ))}
            </div>
        </div>
    );
};
